import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiProviderSiteService } from '../../../../../core/services/api/provider-parking/api-provider-site.service';
import { ProviderSite } from '../../../../../core/models/provider-parking/provider-site.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalProviderSiteComponent } from '../../../modals/provider-parking/modal-provider-site/modal-provider-site.component';

@Component({
  selector: 'app-provider-site',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalProviderSiteComponent],
  templateUrl: './provider-site.component.html',
  styleUrl: './provider-site.component.css'
})
export class ProviderSiteComponent implements OnInit {
  // Data State
  public providerSites: ProviderSite[] = [];
  public isLoading: boolean = false;

  // Dashboard Stats
  public stats = [
    { title: 'พื้นที่ทั้งหมด', value: 0, icon: 'business', color: 'text-blue-600 bg-blue-50' },
    { title: 'สถานะออนไลน์', value: 0, icon: 'cloud_done', color: 'text-emerald-600 bg-emerald-50' },
    { title: 'สถานะออฟไลน์', value: 0, icon: 'cloud_off', color: 'text-rose-600 bg-rose-50' },
    { title: 'จำนวนช่องจอด (Lots)', value: 0, icon: 'local_parking', color: 'text-amber-600 bg-amber-50' }
  ];

  // UI State
  public isModalOpen: boolean = false;
  public selectedSite: ProviderSite | null = null;
  public showFilterPanel: boolean = false;
  public activeDropdown: string | null = null;

  // Search & Filter
  public searchText: string = '';
  public filterProvider: string = '';
  public filterVehicleType: string = '';
  public filterStatus: string | null = null; // 'online' | 'offline' | null
  public filterSite: string = '';

  // Options for filters
  public providerOptions: string[] = [];
  public vehicleTypeOptions: string[] = [];
  public siteOptions: string[] = [];

  public currentPage: number = 1;
  public pageSize: number = 10;
  public pageSizeOptions: number[] = [10, 20, 50, 100];
  public totalItems: number = 0;

  // Sorting
  public sortColumn: string = 'id';
  public sortDirection: 'asc' | 'desc' = 'desc';

  constructor(private apiProviderSiteService: ApiProviderSiteService) { }

  ngOnInit(): void {
    this.getProviderSites();
  }

  public getProviderSites(): void {
    this.isLoading = true;
    this.apiProviderSiteService.getProviderSite().subscribe({
      next: (res) => {
        this.providerSites = res || [];
        this.calculateStats();
        this.extractFilterOptions();
        this.totalItems = this.providerSites.length;
        this.isLoading = false;
      },
      error: (err) => {
        this.loadMockData();
        this.isLoading = false;
      }
    });
  }

  private calculateStats(): void {
    this.stats[0].value = this.providerSites.length;
    this.stats[1].value = this.providerSites.filter(s => s.isOnline).length;
    this.stats[2].value = this.providerSites.filter(s => !s.isOnline).length;
    this.stats[3].value = this.providerSites.reduce((sum, s) => sum + (s.lot || 0), 0);
  }

  private loadMockData(): void {
    this.providerSites = [
      { id: 1, name: 'อาคารจอด The Market 1', lot: 1435, providerName: 'The Market', vehicleType: 'รถยนต์', isOnline: true, site: 'The Market' },
      { id: 2, name: 'ArounD', lot: 1000, providerName: 'AroundD Lifestyle Station', vehicleType: 'รถยนต์', isOnline: false, site: 'AroundD Lifestyle Station' },
      { id: 3, name: 'The Market Moto', lot: 254, providerName: 'The Market', vehicleType: 'จักรยานยนต์', isOnline: false, site: 'The Market' },
      { id: 6, name: 'Siam paragon 3A Floor', lot: 5, providerName: 'Slam Paragon', vehicleType: 'รถยนต์', isOnline: false, site: 'Slam Paragon' },
      { id: 8, name: 'PSS Thailand EV Charger', lot: 3, providerName: 'PSS Group (Thailand)', vehicleType: 'รถยนต์', isOnline: false, site: 'PSS Thailand' }
    ];
    this.calculateStats();
    this.extractFilterOptions();
    this.totalItems = this.providerSites.length;
  }

  // --- Filtering & UI Logic ---

  public toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (!this.showFilterPanel) this.activeDropdown = null;
  }

  public toggleDropdown(type: string, event: Event): void {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === type ? null : type;
  }

  public selectFilter(type: string, value: any): void {
    if (type === 'provider') this.filterProvider = value;
    if (type === 'vehicle') this.filterVehicleType = value;
    if (type === 'status') this.filterStatus = value;
    if (type === 'site') this.filterSite = value;
    
    this.activeDropdown = null;
    this.currentPage = 1;
  }

  public resetFilters(): void {
    this.searchText = '';
    this.filterProvider = '';
    this.filterVehicleType = '';
    this.filterStatus = null;
    this.filterSite = '';
    this.currentPage = 1;
    this.activeDropdown = null;
  }

  public onSearch(): void {
    this.currentPage = 1;
  }

  private extractFilterOptions(): void {
    this.providerOptions = Array.from(new Set(this.providerSites.map(s => s.providerName))).sort();
    this.vehicleTypeOptions = Array.from(new Set(this.providerSites.map(s => s.vehicleType))).sort();
    this.siteOptions = Array.from(new Set(this.providerSites.map(s => s.site))).sort();
  }

  public onRefresh(): void {
    this.getProviderSites();
  }

  public navigateToCreate(): void {
    this.selectedSite = null;
    this.isModalOpen = true;
  }

  public openEditModal(site: ProviderSite): void {
    this.selectedSite = { ...site };
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedSite = null;
  }

  public onSaveSuccess(): void {
    this.getProviderSites();
  }

  public onPageSizeChange(event: any): void {
    this.pageSize = +event.target.value;
    this.currentPage = 1;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  // --- Data Accessors ---

  get filteredData(): ProviderSite[] {
    let data = [...this.providerSites];

    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase().trim();
      data = data.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.providerName.toLowerCase().includes(search) ||
        s.site.toLowerCase().includes(search) ||
        s.id.toString().includes(search)
      );
    }

    if (this.filterProvider) data = data.filter(s => s.providerName === this.filterProvider);
    if (this.filterVehicleType) data = data.filter(s => s.vehicleType === this.filterVehicleType);
    if (this.filterStatus !== null) data = data.filter(s => s.isOnline === (this.filterStatus === 'online'));
    if (this.filterSite) data = data.filter(s => s.site === this.filterSite);

    if (this.sortColumn) {
      data.sort((a: any, b: any) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data;
  }

  get paginatedData(): ProviderSite[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredData.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.pageSize) || 1;
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public trackBySiteId(index: number, item: ProviderSite): number {
    return item.id;
  }

  get startItemIndex(): number {
    return this.filteredData.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    const end = this.currentPage * this.pageSize;
    return end > this.filteredData.length ? this.filteredData.length : end;
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    return Array.from({length: (end - start + 1)}, (_, i) => start + i);
  }
}
